// SIM-1814: accent-green recolor of the white wordmark for the navy header.
import logo from "@/assets/logo-green.png";
import AccountMenu from "@/components/AccountMenu";
import { useTenant } from "@/hooks/useTenant";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@simplicity/ui";
import { ChevronDown, ChevronRight, Home } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

interface AppHeaderProps {
  children?: ReactNode;
  /**
   * SIM-1816: header actions rendered in the right cluster, immediately left of
   * the account avatar (e.g. the SimplySpark Published/Edit mode toggle).
   */
  rightSlot?: ReactNode;
  /**
   * SIM-1816 / SIM-1870: when the SimplySpark nav rail is present it hosts the Home button in its
   * top, so the header suppresses its own Home button to avoid a duplicate. The account menu (incl.
   * the Admin / Super Admin sections) always lives in the header — `AccountMenu` is the single
   * account surface on every authenticated page, so admin links are never duplicated in the rail.
   */
  adminNavInRail?: boolean;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

/**
 * SIM-924: decorative chevron separator between breadcrumb levels in the
 * top-bar. `aria-hidden` so screen-readers don't announce it; the interactive
 * elements on either side retain their own labels.
 */
function BreadcrumbSeparator() {
  return (
    <ChevronRight
      className="h-4 w-4 text-muted-foreground/70 shrink-0"
      aria-hidden="true"
      data-testid="breadcrumb-separator"
    />
  );
}

function TenantSwitcher(props: {
  tenants: Tenant[];
  currentTenantId: string | null;
  currentTenantName: string | null;
  switchTenant: (slug: string) => void;
}) {
  if (props.tenants.length <= 1) {
    if (props.currentTenantName) {
      return <span className="text-base font-medium text-white/80">{props.currentTenantName}</span>;
    }
    return null;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-1.5 text-base font-medium text-white hover:text-white/80 transition-colors outline-hidden">
        {props.currentTenantName ?? "Select client"}
        <ChevronDown className="h-4 w-4 opacity-70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {props.tenants.map((t) => (
          <DropdownMenuItem
            key={t.id}
            onClick={() => props.switchTenant(t.slug)}
            className={`cursor-pointer ${t.id === props.currentTenantId ? "font-bold" : ""}`}
          >
            {t.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// SIM-924: breadcrumb hierarchy. Logo › Home › Tenant › [children = dashboard
// title]. ChevronRight separators are decorative (aria-hidden); each
// interactive element keeps its own aria-label.
function AppHeaderBreadcrumb({
  tenants,
  currentTenantId,
  currentTenantName,
  switchTenant,
  tenantPath,
  showHomeButton,
  children,
}: {
  tenants: ReturnType<typeof useTenant>["tenants"];
  currentTenantId: ReturnType<typeof useTenant>["currentTenantId"];
  currentTenantName: ReturnType<typeof useTenant>["currentTenantName"];
  switchTenant: ReturnType<typeof useTenant>["switchTenant"];
  tenantPath: ReturnType<typeof useTenant>["tenantPath"];
  showHomeButton: boolean;
  children?: ReactNode;
}) {
  const showsTenantBreadcrumb = !!currentTenantName;
  return (
    <div className="flex items-center gap-3" data-testid="app-header-breadcrumb">
      {/* SIM-1898: real anchor (Link via asChild) so cmd/middle-click opens a
          new tab and the URL shows on hover. The logo links to the portal ROOT
          (`/`, above the tenant segment); RootRedirect resolves it to the
          user's first tenant. */}
      <Button asChild variant="ghost" className="p-0 h-auto hover:bg-transparent">
        <Link to="/" aria-label="SimplySpark home">
          <img
            src={logo}
            alt="SimplySpark home"
            // SIM-1693: max-w-none opts out of Tailwind v4 preflight's
            // `img { max-width: 100% }`, which was clamping the logo to its
            // (flex-compressed) parent's width and squishing the 4:1 ratio.
            className="h-9 w-auto shrink-0 max-w-none"
          />
        </Link>
      </Button>
      {/* SIM-1816: the Home button moves into the rail top when a rail is
          present; otherwise it stays here. SIM-1898: the logo navigates to the
          portal root (`/`), while the Home button stays at the tenant home
          (`tenantPath("/")`). Both are real anchors (Link via asChild) so
          open-in-new-tab works. */}
      {showHomeButton && (
        <Button
          asChild
          variant="ghost"
          size="icon"
          className="text-white/80 hover:text-white hover:bg-white/10"
        >
          <Link to={tenantPath("/")} aria-label="Home" title="Home">
            <Home className="h-5 w-5" aria-hidden="true" />
          </Link>
        </Button>
      )}
      {showsTenantBreadcrumb && <BreadcrumbSeparator />}
      <TenantSwitcher
        tenants={tenants}
        currentTenantId={currentTenantId}
        currentTenantName={currentTenantName}
        switchTenant={switchTenant}
      />
      {children && <BreadcrumbSeparator />}
      {children}
    </div>
  );
}

const AppHeader = ({ children, rightSlot, adminNavInRail = false }: AppHeaderProps) => {
  const { tenants, currentTenantId, currentTenantName, switchTenant, tenantPath } = useTenant();

  return (
    <header className="sticky top-0 z-50 px-4 sm:px-8 py-2 border-b border-border flex items-center justify-between bg-primary">
      <AppHeaderBreadcrumb
        tenants={tenants}
        currentTenantId={currentTenantId}
        currentTenantName={currentTenantName}
        switchTenant={switchTenant}
        tenantPath={tenantPath}
        showHomeButton={!adminNavInRail}
      >
        {children}
      </AppHeaderBreadcrumb>
      <div className="flex items-center gap-3">
        {rightSlot}
        {/* SIM-1870: the single shared account menu — identity + role-gated
            Admin / Super Admin sections + Help + Sign out. Reachable on every
            authenticated page because every page renders AppHeader. */}
        <AccountMenu />
      </div>
    </header>
  );
};

export default AppHeader;
